import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AxiosError } from "axios";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";

import { useTakeActionModal } from "./use-action-modal";
import { Button } from "@/components/ui/button/button";
import { takeActionOnLoanRequest } from "@/config/mutation/admin";
import { Input } from "@/components/ui/input/input";
import { Separator } from "@/components/ui/separator/separator";

export const TakeActionOnLoanRequesetModal = () => {
  const queryClient = useQueryClient();
  const key = Cookies.get("key") as string;
  const { open, onOpenChange, loan } = useTakeActionModal();

  const [formValues, setFormValues] = useState({
    totalAmountApproved: loan?.totalAmountRequested,
    interestRate: 0,
    status: "",
    numberOfMonths: 0,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: takeActionOnLoanRequest(key),
  });

  const submitAction = async () => {
    if (formValues.status === "") {
      return toast.error("Please provide status");
    }

    if (formValues.status === "Approved") {
      if (formValues.interestRate === 0) {
        return toast.error("Please provide interest rate");
      }
      if (formValues.totalAmountApproved === 0) {
        return toast.error("Please provide total amount approved");
      }

      if (formValues.numberOfMonths === 0) {
        return toast.error("Please provide number of months");
      }
    }

    mutate(
      {
        loanId: loan?.id as string,
        status: formValues.status,
        totalAmountApproved: formValues.totalAmountApproved as number,
        interestRate: formValues.interestRate,
        numberOfMonths: formValues.numberOfMonths,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["loan"],
          });

          toast.success("Action Taken.");
          onOpenChange(false);
        },

        onError: (error) => {
          console.log(
            "ERROR WHILE CREATING TAKING ACTION ON LOAN REQUEST",
            error
          );
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  useEffect(() => {
    setFormValues((prev) => {
      return {
        ...prev,
        totalAmountApproved: loan?.totalAmountRequested,
        interestRate: 0,
        numberOfMonths: 0,
      };
    });
  }, [loan]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="tracking-normal">
              Take action on loan request
            </DialogTitle>
          </DialogHeader>

          <div>
            <Select
              value={formValues.status}
              onValueChange={(value) => {
                setFormValues((prev) => {
                  return {
                    ...prev,
                    status: value,
                  };
                });
              }}
              disabled={isPending}
            >
              <span className="mb-2 inline-block">Choose Status</span>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Action..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Approved">Approved</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formValues.status === "Approved" && (
            <>
              <Separator />
              <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label htmlFor="totalAmountApproved">
                    Total Amount Approved
                  </label>
                  <Input
                    id="totalAmountApproved"
                    type="number"
                    min={0}
                    disabled={true}
                    name="totalAmountApproved"
                    value={formValues.totalAmountApproved}
                    // onChange={(e) => {
                    //   setFormValues((prev) => {
                    //     return {
                    //       ...prev,
                    //       [e.target.name]: e.target.value,
                    //     };
                    //   });
                    // }}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="interestRate">Interest Rate</label>
                  <Input
                    id="interestRate"
                    type="number"
                    min={0}
                    disabled={isPending}
                    name="interestRate"
                    value={formValues.interestRate}
                    onChange={(e) => {
                      setFormValues((prev) => {
                        return {
                          ...prev,
                          [e.target.name]: e.target.value,
                        };
                      });
                    }}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="numberOfMonths">Number of Months</label>
                  <Input
                    id="numberOfMonths"
                    type="number"
                    min={0}
                    disabled={isPending}
                    name="numberOfMonths"
                    value={formValues.numberOfMonths}
                    onChange={(e) => {
                      setFormValues((prev) => {
                        return {
                          ...prev,
                          [e.target.name]: e.target.value,
                        };
                      });
                    }}
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex items-center justify-end">
            <Button onClick={submitAction} disabled={isPending}>
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
